// Grids & Gantts — a popover on a desk, a sheet on a phone.
//
// Standard 1.10: A MENU OPENS WHERE THE HAND IS.
//
// Every menu on a grid page hangs off the thing it belongs to: a column
// heading, a status chip, a cell. That is right at a desk — the menu appears
// beside what it is about, and the eye does not have to go looking.
//
// On a 375px screen it is wrong twice over. A popover anchored under a
// trigger near the foot of the screen opens off the bottom of it, and there
// is no room beside it to clamp into; and even when it fits, it opens under
// the finger that is covering it. A sheet rising from the bottom edge is
// always where the thumb already is, always has room, and is what every
// phone in the world has trained people to expect.
//
// So: the same menu, anchored on a desk and a sheet on a phone. It is one
// component because it is one menu — only where it lands changes.
//
// PORTALLED, ALWAYS, and the reason is worth writing down because the symptom
// lies. These menus open from inside a grid row, and a row's frozen column
// pane is `position: sticky; z-index: 1` — which creates a STACKING CONTEXT.
// Inside one, a child's z-index is only compared with its siblings, so
// `z-index: 4000` on the menu competes with nothing useful and every row
// painted after it goes on top. The menu is still there and still has its own
// background; it is simply underneath the rest of the grid, so it reads as
// washed-out text that cannot be clicked. No z-index fixes that — the element
// has to leave the stacking context altogether, which is what a portal does.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FONT } from '../structure/theme';

export default function Sheet({
  anchorRef, onClose, ui, children, width = 260, narrow = false, title = '', maxHeight = 330,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const off = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [anchorRef, onClose]);

  const box = {
    background: ui.card,
    color: ui.fg,
    border: `1px solid ${ui.border}`,
    fontFamily: FONT,
    overflowY: 'auto',
  };

  if (narrow) {
    return createPortal((
      <>
        {/* The page behind a sheet is not a target. A scrim also says, in the
            one way that needs no words, that this is modal. */}
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 3999, background: 'rgba(17, 17, 26, 0.35)' }} />
        <div ref={ref} style={{
          ...box,
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4000,
          maxHeight: '70vh',
          borderRadius: '14px 14px 0 0',
          borderBottom: 'none',
          boxShadow: '0 -8px 28px rgba(0,0,0,0.28)',
          padding: '10px 12px calc(12px + env(safe-area-inset-bottom, 0px))',
        }}>
          {/* A WAY OUT THAT IS NOT "TAP THE BIT YOU CANNOT SEE". There is no
              Escape key on a phone and the scrim is behind the thumb. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
            paddingBottom: 8, borderBottom: `1px solid ${ui.border}`,
          }}>
            <strong style={{ fontSize: 13 }}>{title || 'Options'}</strong>
            <button onClick={onClose} style={{
              marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, padding: '6px 14px',
              borderRadius: 999, border: `1px solid ${ui.border}`, background: ui.card,
              color: ui.muted, cursor: 'pointer', fontFamily: FONT,
            }}>Done</button>
          </div>
          {children}
        </div>
      </>
    ), document.body);
  }

  const r = anchorRef && anchorRef.current ? anchorRef.current.getBoundingClientRect() : { left: 0, bottom: 0, top: 0 };
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  // Near the foot of the window it opens UPWARD, rather than off the end.
  const openUp = r.bottom > vh - (maxHeight + 24);
  return createPortal((
    <div ref={ref} style={{
      ...box,
      position: 'fixed',
      ...(openUp ? { bottom: Math.max(8, vh - r.top + 4) } : { top: r.bottom + 2 }),
      left: Math.max(8, Math.min(r.left, vw - width - 12)),
      width, zIndex: 4000, maxHeight,
      borderRadius: 8,
      boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
      padding: 8,
    }}>{children}</div>
  ), document.body);
}
